// ============================================================
// Dashboard.jsx  –  Fresh Hills Admin Dashboard
// ✅ 100% real backend data — zero hardcoded fake values
// ✅ Fully responsive: 320px → 1440px
// ✅ Real API: getDailySales, getFridges, getStock, getRefills
// ✅ Weekly chart from real sales data
// ✅ Low-stock from real stock API
// ✅ Refill schedule from real refills API
// ============================================================
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import {
  ShoppingBagIcon, CubeIcon, CurrencyRupeeIcon,
  BuildingStorefrontIcon, ArrowTrendingUpIcon,
  ExclamationTriangleIcon, ClockIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import {
  getDailySales, getFridges, getStock, getRefills,
} from '../../services/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const Dashboard = () => {
  const navigate = useNavigate();

  const [salesData,  setSalesData]  = useState(null);
  const [fridges,    setFridges]    = useState([]);
  const [lowStock,   setLowStock]   = useState([]);
  const [refills,    setRefills]    = useState([]);
  const [weekChart,  setWeekChart]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    const [salesR, fridgesR, stockR, refillsR] = await Promise.allSettled([
      getDailySales(),
      getFridges(),
      getStock(),
      getRefills(),
    ]);

    /* ── sales ── */
    if (salesR.status === 'fulfilled') {
      const d = salesR.value.data;
      const s = d.sales || d;
      setSalesData(s);
      // weekly chart: try several field names APIs commonly use
      const w = s.daily_breakdown || s.weekly || s.weekly_data || d.weekly || [];
      setWeekChart(w);
    }

    /* ── fridges ── */
    if (fridgesR.status === 'fulfilled')
      setFridges(fridgesR.value.data.fridges || []);

    /* ── low stock ── */
    if (stockR.status === 'fulfilled') {
      const items = stockR.value.data.stock || stockR.value.data.items || [];
      setLowStock(
        items.filter(s =>
          Number(s.quantity) <= Number(s.min_quantity || s.min_stock_level || 5)
        )
      );
    }

    /* ── today's refills ── */
    if (refillsR.status === 'fulfilled') {
      const all = refillsR.value.data.refills || [];
      const today = new Date().toISOString().split('T')[0];
      setRefills(
        all.filter(r => {
          const d = r.scheduled_date || r.refill_date || r.date || '';
          return d.startsWith(today) || r.status === 'pending' || r.status === 'scheduled';
        }).slice(0, 5)
      );
    }

    setLastUpdate(new Date());
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  /* ── Chart data built from REAL API ── */
  const DAY_ABBR = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const chartLabels = weekChart.length
    ? weekChart.map(d => {
        if (d.day_name) return d.day_name.slice(0,3);
        if (d.date)     return new Date(d.date).toLocaleDateString('en-IN',{weekday:'short'});
        return DAY_ABBR[weekChart.indexOf(d)] || '';
      })
    : DAY_ABBR;

  const chartValues = weekChart.length
    ? weekChart.map(d => Number(d.revenue || d.total_revenue || d.amount || 0))
    : new Array(7).fill(0);

  const chartData = {
    labels: chartLabels,
    datasets: [{
      label: 'Revenue (₹)',
      data: chartValues,
      borderColor: '#059669',
      backgroundColor: 'rgba(5,150,105,0.08)',
      tension: 0.4, fill: true,
      pointBackgroundColor: '#059669',
      pointBorderColor: 'white',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: c => `₹${Number(c.raw).toLocaleString('en-IN')}` },
      },
    },
    scales: {
      y: {
        grid:  { color: '#f1f5f9' },
        ticks: {
          color: '#64748b', font: { family:'Nunito', weight:'700', size:11 },
          callback: v => v >= 1000 ? `₹${(v/1000).toFixed(1)}k` : `₹${v}`,
        },
        beginAtZero: true,
      },
      x: {
        grid:  { display: false },
        ticks: { color:'#64748b', font:{ family:'Nunito', weight:'700', size:11 } },
      },
    },
  };

  /* ── Stat cards — all real values ── */
  const totalRevenue  = salesData?.total_revenue || salesData?.revenue || 0;
  const totalOrders   = salesData?.total_orders  || salesData?.orders  || 0;
  const selfService   = salesData?.self_service_orders || salesData?.self_service || 0;
  const activeFridges = fridges.filter(f => !f.status || f.status === 'active').length;

  const STATS = [
    { title:"Today's Revenue", value:`₹${Number(totalRevenue).toLocaleString('en-IN')}`, icon:CurrencyRupeeIcon,      color:'#059669', bg:'#ecfdf5', border:'#a7f3d0' },
    { title:'Total Orders',    value:Number(totalOrders).toLocaleString('en-IN'),         icon:ShoppingBagIcon,        color:'#2563eb', bg:'#eff6ff', border:'#bfdbfe' },
    { title:'Active Fridges',  value:activeFridges,                                        icon:BuildingStorefrontIcon, color:'#7c3aed', bg:'#faf5ff', border:'#ddd6fe' },
    {
      title:'Low Stock Alerts',
      value: lowStock.length,
      icon:ExclamationTriangleIcon,
      color: lowStock.length > 0 ? '#dc2626' : '#059669',
      bg:    lowStock.length > 0 ? '#fff1f2' : '#ecfdf5',
      border:lowStock.length > 0 ? '#fecdd3' : '#a7f3d0',
    },
  ];

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Nunito',sans-serif;}

    /* ─── PAGE HEAD ──────────────────────────────────────────── */
    .db-ph{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1rem;gap:0.75rem;flex-wrap:wrap;}
    @media(min-width:480px){.db-ph{margin-bottom:1.35rem;}}
    @media(min-width:768px){.db-ph{margin-bottom:2rem;}}
    .db-title{font-size:1.2rem;font-weight:900;color:#111827;}
    @media(min-width:480px){.db-title{font-size:1.4rem;}}
    @media(min-width:768px){.db-title{font-size:1.6rem;}}
    .db-sub{font-size:0.72rem;color:#64748b;font-weight:600;margin-top:0.1rem;}
    @media(min-width:480px){.db-sub{font-size:0.8rem;}}
    @media(min-width:768px){.db-sub{font-size:0.88rem;}}
    .db-upd{font-size:0.6rem;color:#94a3b8;font-weight:600;margin-top:0.2rem;}
    @media(min-width:480px){.db-upd{font-size:0.66rem;}}
    .db-ref{display:flex;align-items:center;gap:0.3rem;padding:0.34rem 0.72rem;border-radius:9999px;background:#ecfdf5;color:#059669;border:1.5px solid #a7f3d0;font-weight:700;font-size:0.7rem;cursor:pointer;font-family:'Nunito',sans-serif;transition:all 0.2s;white-space:nowrap;flex-shrink:0;}
    @media(min-width:480px){.db-ref{padding:0.4rem 0.9rem;font-size:0.76rem;}}
    @media(min-width:768px){.db-ref{padding:0.44rem 1rem;font-size:0.8rem;}}
    .db-ref svg{width:0.78rem;height:0.78rem;}
    .db-ref:hover{background:#059669;color:white;}

    /* ─── STATS GRID ─────────────────────────────────────────── */
    .db-sg{display:grid;grid-template-columns:repeat(2,1fr);gap:0.65rem;margin-bottom:1rem;}
    @media(min-width:480px){.db-sg{gap:0.85rem;margin-bottom:1.35rem;}}
    @media(min-width:768px){.db-sg{gap:1.1rem;margin-bottom:1.75rem;}}
    @media(min-width:1024px){.db-sg{grid-template-columns:repeat(4,1fr);gap:1.25rem;margin-bottom:2rem;}}

    .db-sc{background:white;border-radius:1rem;padding:0.85rem;border:1.5px solid;box-shadow:0 4px 14px rgba(0,0,0,0.04);display:flex;align-items:center;justify-content:space-between;gap:0.5rem;transition:all 0.3s;}
    @media(min-width:480px){.db-sc{padding:1.1rem;border-radius:1.25rem;}}
    @media(min-width:768px){.db-sc{padding:1.4rem;border-radius:1.4rem;}}
    @media(min-width:1200px){.db-sc{padding:1.5rem;border-radius:1.5rem;}}
    .db-sc:hover{transform:translateY(-3px);box-shadow:0 10px 22px rgba(0,0,0,0.08);}
    .db-slbl{font-size:0.62rem;font-weight:700;color:#64748b;margin-bottom:0.25rem;line-height:1.25;}
    @media(min-width:480px){.db-slbl{font-size:0.7rem;margin-bottom:0.32rem;}}
    @media(min-width:768px){.db-slbl{font-size:0.78rem;margin-bottom:0.38rem;}}
    @media(min-width:1200px){.db-slbl{font-size:0.8rem;margin-bottom:0.4rem;}}
    .db-sval{font-size:1.2rem;font-weight:900;color:#111827;}
    @media(min-width:480px){.db-sval{font-size:1.45rem;}}
    @media(min-width:768px){.db-sval{font-size:1.65rem;}}
    @media(min-width:1200px){.db-sval{font-size:1.8rem;}}
    .db-sic{width:36px;height:36px;border-radius:0.7rem;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
    @media(min-width:480px){.db-sic{width:42px;height:42px;border-radius:0.85rem;}}
    @media(min-width:768px){.db-sic{width:48px;height:48px;border-radius:0.95rem;}}
    @media(min-width:1200px){.db-sic{width:52px;height:52px;border-radius:1rem;}}
    .db-sic svg{width:1rem;height:1rem;}
    @media(min-width:480px){.db-sic svg{width:1.2rem;height:1.2rem;}}
    @media(min-width:768px){.db-sic svg{width:1.4rem;height:1.4rem;}}
    @media(min-width:1200px){.db-sic svg{width:1.5rem;height:1.5rem;}}

    /* ─── MAIN GRID ──────────────────────────────────────────── */
    .db-mg{display:grid;grid-template-columns:1fr;gap:0.85rem;}
    @media(min-width:480px){.db-mg{gap:1.1rem;}}
    @media(min-width:1024px){.db-mg{grid-template-columns:2fr 1fr;gap:1.5rem;}}

    /* ─── CARD ───────────────────────────────────────────────── */
    .db-card{background:white;border-radius:1rem;padding:1rem;border:1px solid #f1f5f9;box-shadow:0 4px 14px rgba(0,0,0,0.04);}
    @media(min-width:480px){.db-card{padding:1.25rem;border-radius:1.25rem;}}
    @media(min-width:768px){.db-card{padding:1.5rem;border-radius:1.5rem;}}
    .db-ct{font-size:0.88rem;font-weight:800;color:#111827;margin-bottom:0.85rem;display:flex;align-items:center;gap:0.4rem;}
    @media(min-width:480px){.db-ct{font-size:0.95rem;margin-bottom:1rem;}}
    @media(min-width:768px){.db-ct{font-size:1rem;margin-bottom:1.25rem;}}
    .db-ct svg{width:0.95rem;height:0.95rem;color:#059669;flex-shrink:0;}
    @media(min-width:768px){.db-ct svg{width:1.1rem;height:1.1rem;}}

    /* Right col */
    .db-rc{display:flex;flex-direction:column;gap:0.85rem;}
    @media(min-width:480px){.db-rc{gap:1.1rem;}}
    @media(min-width:1024px){.db-rc{gap:1.5rem;}}

    /* Quick actions */
    .db-qb{width:100%;display:flex;align-items:center;justify-content:space-between;padding:0.62rem 0.85rem;border-radius:0.78rem;font-weight:700;font-size:0.78rem;border:1.5px solid;cursor:pointer;margin-bottom:0.55rem;transition:all 0.2s;font-family:'Nunito',sans-serif;}
    @media(min-width:480px){.db-qb{padding:0.7rem 0.95rem;font-size:0.82rem;border-radius:0.85rem;margin-bottom:0.65rem;}}
    @media(min-width:768px){.db-qb{padding:0.75rem 1rem;font-size:0.86rem;border-radius:0.875rem;margin-bottom:0.72rem;}}
    .db-qb:last-child{margin-bottom:0;}
    .db-qb:hover{transform:translateX(4px);}
    .db-qb svg{width:0.82rem;height:0.82rem;flex-shrink:0;}

    /* List rows */
    .db-row{display:flex;align-items:center;justify-content:space-between;padding:0.5rem 0;border-bottom:1px solid #f1f5f9;font-size:0.76rem;gap:0.5rem;}
    @media(min-width:480px){.db-row{padding:0.58rem 0;font-size:0.8rem;}}
    @media(min-width:768px){.db-row{padding:0.62rem 0;font-size:0.84rem;}}
    .db-row:last-child{border-bottom:none;}
    .db-rn{font-weight:700;color:#374151;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
    .db-pill-red{background:#fff1f2;color:#dc2626;font-weight:700;font-size:0.62rem;padding:0.15rem 0.52rem;border-radius:9999px;flex-shrink:0;white-space:nowrap;}
    .db-pill-grn{background:#ecfdf5;color:#059669;font-weight:700;font-size:0.62rem;padding:0.15rem 0.52rem;border-radius:9999px;flex-shrink:0;white-space:nowrap;}
    @media(min-width:480px){.db-pill-red,.db-pill-grn{font-size:0.68rem;padding:0.18rem 0.6rem;}}
    @media(min-width:768px){.db-pill-red,.db-pill-grn{font-size:0.72rem;padding:0.2rem 0.65rem;}}

    /* No-data box */
    .db-nd{padding:1.5rem;text-align:center;color:#94a3b8;font-size:0.78rem;font-weight:600;background:#f8fafc;border-radius:0.75rem;}
    @media(min-width:480px){.db-nd{padding:2rem;font-size:0.82rem;border-radius:0.875rem;}}

    /* Chart no-data */
    .db-cw{position:relative;}
    .db-cnd{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.75);border-radius:0.75rem;font-size:0.8rem;font-weight:700;color:#94a3b8;}

    /* Loading */
    .db-ld{display:flex;justify-content:center;align-items:center;height:60vh;}
    .db-spin{width:2.5rem;height:2.5rem;border-radius:50%;border:3px solid #f3f4f6;border-top-color:#059669;animation:dbsp 0.7s linear infinite;}
    @media(min-width:480px){.db-spin{width:3rem;height:3rem;}}
    @keyframes dbsp{to{transform:rotate(360deg);}}
  `;

  if (loading) return (
    <AdminLayout>
      <style>{css}</style>
      <div className="db-ld"><div className="db-spin" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <style>{css}</style>

      {/* PAGE HEADER */}
      <div className="db-ph">
        <div>
          <div className="db-title">📊 Dashboard</div>
          <div className="db-sub">Welcome back! Here's what's happening today.</div>
          {lastUpdate && <div className="db-upd">Updated: {lastUpdate.toLocaleTimeString('en-IN')}</div>}
        </div>
        <button className="db-ref" onClick={loadAll}>
          <ArrowPathIcon /> Refresh
        </button>
      </div>

      {/* STATS — all real API values */}
      <div className="db-sg">
        {STATS.map(s => (
          <div key={s.title} className="db-sc" style={{ borderColor:s.border }}>
            <div>
              <div className="db-slbl">{s.title}</div>
              <div className="db-sval">{s.value}</div>
            </div>
            <div className="db-sic" style={{ background:s.bg }}>
              <s.icon style={{ color:s.color }} />
            </div>
          </div>
        ))}
      </div>

      {/* MAIN GRID */}
      <div className="db-mg">

        {/* Weekly chart — real sales data */}
        <div className="db-card">
          <div className="db-ct"><ArrowTrendingUpIcon /> Weekly Revenue</div>
          <div className="db-cw">
            <Line data={chartData} options={chartOptions} />
            {chartValues.every(v => v === 0) && (
              <div className="db-cnd">No revenue data available yet</div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="db-rc">

          {/* Quick actions */}
          <div className="db-card">
            <div className="db-ct"><CubeIcon /> Quick Actions</div>
            {[
              { label:'Manage Products',  route:'/admin/products',         bg:'#eff6ff', color:'#2563eb', border:'#bfdbfe' },
              { label:'Manage Fridges',   route:'/admin/fridges',          bg:'#ecfdf5', color:'#059669', border:'#a7f3d0' },
              { label:'Assign Products',  route:'/admin/assign-products',  bg:'#faf5ff', color:'#7c3aed', border:'#ddd6fe' },
              { label:'Stock Monitor',    route:'/admin/stock-monitoring', bg:'#fff7ed', color:'#ea580c', border:'#fed7aa' },
              { label:'Sales Report',     route:'/admin/sales-report',     bg:'#f0fdf4', color:'#16a34a', border:'#bbf7d0' },
            ].map(b => (
              <button key={b.route} className="db-qb"
                style={{ background:b.bg, color:b.color, borderColor:b.border }}
                onClick={() => navigate(b.route)}>
                {b.label}
                <ArrowTrendingUpIcon />
              </button>
            ))}
          </div>

          {/* Low stock — real stock API */}
          <div className="db-card">
            <div className="db-ct"><ExclamationTriangleIcon /> Low Stock Alerts</div>
            {lowStock.length === 0 ? (
              <div className="db-nd">✅ All stock levels are healthy</div>
            ) : lowStock.slice(0, 6).map((item, i) => (
              <div key={i} className="db-row">
                <span className="db-rn">
                  {item.fridge_id || item.fridge_name || 'Fridge'}
                  {item.product_name || item.name ? ` — ${item.product_name || item.name}` : ''}
                </span>
                <span className="db-pill-red">{item.quantity} left</span>
              </div>
            ))}
          </div>

          {/* Refill schedule — real refills API */}
          <div className="db-card">
            <div className="db-ct"><ClockIcon /> Today's Refill Schedule</div>
            {refills.length === 0 ? (
              <div className="db-nd">No refills scheduled today</div>
            ) : refills.map((r, i) => {
              const time = r.scheduled_time || r.time
                || (r.scheduled_date
                  ? new Date(r.scheduled_date).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})
                  : 'Pending');
              const name = r.fridge_id || r.fridge_name || r.location || `Refill #${r.id || i+1}`;
              return (
                <div key={i} className="db-row">
                  <span className="db-rn">{name}</span>
                  <span className="db-pill-grn">{time}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;